import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Campaign } from 'features/Yucollect/utils/types';
import {
  DispositionWiseSummary,
  IAddAlternateNumberRequest,
  IAddAlternateNumberResponse,
  IAddDispositionRequest,
  ICallBargingRequest,
  ICallBargingResponse,
  IClickToCallRequest,
  IClickToCallResponse,
  IFetchAdditionalInfoRequest,
  IFetchAdditionalInfoResponse,
  IFetchBestAndLastDispositionRequest,
  IFetchBestAndLastDispositionResponse,
  IFetchBorrowerInfoRequest,
  IFetchBorrowerInfoResponse,
  IFetchBorrowersInCurrentCampaignRequest,
  IFetchBorrowersInCurrentCampaignResponse,
  IFetchCampaignsByAgentRequest,
  IFetchCampaignsByAgentResponse,
  IFetchDispositionWiseSummaryRequest,
  IFetchDispositionWiseSummaryResponse,
  IFetchDispositionsResponse,
  IFetchFollowUpTimeRangesRequest,
  IFetchFollowUpTimeRangesResponse,
  IFetchInteractionHistoryRequest,
  IFetchInteractionHistoryResponse,
  IFetchMissedCallsDistinctRequest,
  IFetchMissedCallsRequest,
  IFetchMissedCallsResponse,
  IFetchPreviousOrNextBorrowerRequest,
  IFetchPreviousOrNextBorrowerResponse,
  IFetchPriorityTasksDistinctRequest,
  IFetchPriorityTasksRequest,
  IFetchPriorityTasksResponse,
  IFetchPriorityTasksSummaryRequest,
  IFetchWhatsappBadgeCountResponse,
  IFetchWhatsappMessagesRequest,
  IGetRemarkTemplatesResponse,
  IGetUniqueValuesByColumnNameRequest,
  ISaveCampaignRequest,
  ISaveCampaignResponse,
  ISearchInCrmDataGridRequest,
  ISearchInFilterByValuesRequest,
  ISearchInFilterByValuesResponse,
  IUpdateAlternateNumberRequest,
  IUpdateAlternateNumberResponse,
  IWhatsappMessageRecord,
  IWhatsappMessagesResponse,
  MissedCallDistinctItemWithId,
  PriorityTasksSummary,
} from 'redux/types/CRM.types';
import { IFailedPayload } from 'redux/types/Call.types';
import {
  ACW,
  AGENT_CONNECTED,
  AGENT_CONNECTING,
  AGENT_HOLD,
  AGENT_MUTE,
  AGENT_RINGING,
  AGENT_RINING,
  BIOMETRIC_BREAK,
  BREAK,
  CALLBACK,
  CONNECTED,
  CUSTOMER_CONNECTED,
  CUSTOMER_CONNECTING,
  CUSTOMER_DISCONNECTED,
  CUSTOMER_DISCONNECTING,
  CUSTOMER_RINGING,
  CUSTOMER_RINING,
  HOLD,
  LOGOUT,
  LUNCH_BREAK,
  MEETING_BREAK,
  MOBILE_BREAK,
  MUTE,
  OFFLINE,
  ONLINE,
  PREVIEW,
  PREVIEW_CAPITALIZE,
  TEA_BREAK,
  TRANSFER_CALL,
  TRANSFER_START,
  TRANSFER_TALK,
} from 'utils/call-status';

// Define agent status type using constants from call-status.ts as the source of truth
export type AgentStatusType =
  | typeof ACW
  | typeof PREVIEW
  | typeof PREVIEW_CAPITALIZE
  | typeof OFFLINE
  | typeof CALLBACK
  | typeof ONLINE
  | typeof BREAK
  | typeof LUNCH_BREAK
  | typeof TEA_BREAK
  | typeof MEETING_BREAK
  | typeof BIOMETRIC_BREAK
  | typeof MOBILE_BREAK
  | typeof LOGOUT
  | typeof TRANSFER_START
  | typeof TRANSFER_CALL
  | typeof TRANSFER_TALK
  | typeof AGENT_CONNECTING
  | typeof AGENT_RINING
  | typeof AGENT_RINGING
  | typeof AGENT_CONNECTED
  | typeof CUSTOMER_CONNECTING
  | typeof CUSTOMER_RINING
  | typeof CUSTOMER_RINGING
  | typeof CUSTOMER_CONNECTED
  | typeof CONNECTED
  | typeof CUSTOMER_DISCONNECTED
  | typeof CUSTOMER_DISCONNECTING
  | typeof AGENT_MUTE
  | typeof MUTE
  | typeof AGENT_HOLD
  | typeof HOLD;

export interface AgentStateType {
  status: AgentStatusType;
  displayStatus:
    | typeof OFFLINE
    | typeof ONLINE
    | typeof BREAK
    | typeof LUNCH_BREAK
    | typeof TEA_BREAK
    | typeof MEETING_BREAK
    | typeof BIOMETRIC_BREAK
    | typeof MOBILE_BREAK;
  subStatus?:
    | typeof LUNCH_BREAK
    | typeof TEA_BREAK
    | typeof MEETING_BREAK
    | typeof BIOMETRIC_BREAK
    | typeof MOBILE_BREAK;
}

interface IAgentInfo {
  name: string | null;
  agentVerificationKey: string | null;
  yucollectEmpId: string | null;
  supervisorVerificationKey: string | null;
}

// Interface for disposition reminder data
interface DispositionReminderData {
  borrowerId: string;
  campaignId: string;
  type: string;
  fromPage: string;
}
/**
 * Enhanced CallStatusData interface with additional Tubelight call data
 * Using CallStatusData from CRM.types.ts as the base
 */
export interface CRMCallStatusData {
  status: string;
  startTime: number;
  duration?: number | null;
  callId: string;
  agentId: string;
  agentName: string;
  campaignId: string;
  borrowerId: string;
  borrowerName: string;
  accountNumber: string;
  phoneNumber: string;
  product: string;
  bucket: string;
  location: string;
  totalOutstandingAmount: number;
}

export interface IAgentStatusError {
  message: string;
  code?: string;
  details?: string;
}

// Navigation payload interface for redux actions
export interface NavigationPayload {
  path: string;
  params?: Record<string, string>;
}

export enum CRMTab {
  ALL_ACCOUNTS = 'all_accounts',
  FOLLOW_UP_ACCOUNTS = 'follow_up_accounts',
  EXPIRED_ACCOUNTS = 'expired_accounts',
}

export interface CRMInitState {
  error: IFailedPayload | null;
  fetchCampaignsByAgentError: IFailedPayload | null;
  fetchBorrowersByCampaignError: IFailedPayload | null;
  loading: boolean;
  isCRMDataGridLoading: boolean;
  areCampaignsLoading: boolean;
  saveCampaignCompleted: boolean;
  isDispositionWiseSummaryLoading: boolean;
  // Agent status related fields
  isAgentStatusLoading: boolean;
  agentStatusError: string | null;
  campaigns: IFetchCampaignsByAgentResponse[];
  borrowers: IFetchBorrowersInCurrentCampaignResponse | null;
  clickToCallResponse: IClickToCallResponse | null;
  borrowerInfo: IFetchBorrowerInfoResponse | null;
  additionalInfo: IFetchAdditionalInfoResponse | null;
  areAdditionalInfoLoading: boolean;
  agentInfo: IAgentInfo;
  showIframe: boolean;
  dispositions: IFetchDispositionsResponse;
  dispositionWiseSummary: IFetchDispositionWiseSummaryResponse;
  message: string;
  bestAndLastDisposition: IFetchBestAndLastDispositionResponse | null;
  activityHistory: IFetchInteractionHistoryResponse;
  currentCampaign: Campaign | null;
  currentDisposition: DispositionWiseSummary;
  isAddDispositionLoading: boolean;
  crmActiveTab: CRMTab;
  followUpTimeRanges: IFetchFollowUpTimeRangesResponse[] | null;
  followUpCurrentTime: IFetchFollowUpTimeRangesResponse | null;
  uniqueColumnValues: string[];
  totalRecords: number;
  agentState: AgentStateType;
  agentLastSavedStatus: typeof ONLINE | typeof OFFLINE;
  supervisorLastSavedStatus: typeof ONLINE | typeof OFFLINE;
  // Modal states
  isContactDetailsModalOpen: boolean;
  isAddAlternateNumberModalOpen: boolean;
  isSuccessModalOpen: boolean;
  selectedAlternateNumber: string;
  isEditMode: boolean;
  remarkTemplates: IGetRemarkTemplatesResponse[];
  missedCalls: IFetchMissedCallsResponse;
  latestSubDispositionId: string | null;
  // Disposition reminder fields
  showDispositionReminder: boolean;
  dispositionReminderData: DispositionReminderData | null;
  callData: CRMCallStatusData | null;
  resetFiltersTimestamp: number;
  // Barge call fields
  isCallBargingLoading: boolean;
  callBargingResponse: ICallBargingResponse | null;
  // Previous/Next borrower fields
  isFetchingPreviousOrNextBorrower: boolean;
  previousOrNextBorrowerResponse: IFetchPreviousOrNextBorrowerResponse | null;
  // Navigation state
  navigation: NavigationPayload | null;
  acwStartTime: number | null;
  microphoneError: {
    open: boolean;
    message: string;
  };
  allocatorError: {
    open: boolean;
    message: string;
  };
  isAgentInManualCall: boolean;
  // True while a supervisor "Call Barge" session is active. Used to resolve the
  // barged borrower directly (skipping the phone-number lookup) on connect.
  isCallBarging: boolean;
  bargeBorrowerId: string;
  bargeCampaignId: string;
  isUserInitiatedOnlineTransition: boolean;
  // Priority Tasks
  priorityTasks: IFetchPriorityTasksResponse;
  priorityTasksSummary: PriorityTasksSummary | null;
  priorityTasksDistinct: string[];
  isPriorityTasksLoading: boolean;
  isPriorityTasksSummaryLoading: boolean;
  isPriorityTasksDistinctLoading: boolean;
  // Missed Calls Distinct
  missedCallsDistinct: (string | MissedCallDistinctItemWithId)[];
  isMissedCallsDistinctLoading: boolean;
  // WhatsApp Messages
  whatsappMessages: {
    data: IWhatsappMessageRecord[];
    pagination: { page: number; size: number };
    totalElements: number;
    totalPages: number;
  };
  whatsappBadgeCount: number;
  isWhatsappMessagesLoading: boolean;
  isWhatsappBadgeCountLoading: boolean;
}

export const crmInitState: CRMInitState = {
  error: null,
  fetchCampaignsByAgentError: null,
  fetchBorrowersByCampaignError: null,
  loading: false,
  isCRMDataGridLoading: false,
  areCampaignsLoading: false,
  saveCampaignCompleted: false,
  isDispositionWiseSummaryLoading: false,
  campaigns: [],
  borrowers: null,
  clickToCallResponse: null,
  borrowerInfo: null,
  additionalInfo: null,
  areAdditionalInfoLoading: false,
  showIframe: false,
  agentInfo: {
    name: null,
    agentVerificationKey: null,
    yucollectEmpId: null,
    supervisorVerificationKey: null,
  },
  dispositions: { dispositions: [] },
  dispositionWiseSummary: { dispositions: [] },
  message: '',
  bestAndLastDisposition: null,
  activityHistory: {
    status: '',
    message: '',
    totalRecords: 0,
    data: [],
  },
  currentCampaign: null,
  currentDisposition: {
    name: '',
    borrowerCount: 0,
    TOS: 0,
    percentage: 0,
    id: [],
  },
  isAddDispositionLoading: false,
  crmActiveTab: CRMTab.ALL_ACCOUNTS,
  followUpTimeRanges: null,
  followUpCurrentTime: null,
  uniqueColumnValues: [],
  totalRecords: 0,
  agentState: {
    status: OFFLINE,
    displayStatus: OFFLINE,
    subStatus: undefined,
  },
  agentLastSavedStatus: OFFLINE,
  supervisorLastSavedStatus: OFFLINE,
  isAgentStatusLoading: false,
  agentStatusError: null,
  // Modal states
  isContactDetailsModalOpen: false,
  isAddAlternateNumberModalOpen: false,
  isSuccessModalOpen: false,
  selectedAlternateNumber: '',
  isEditMode: false,
  remarkTemplates: [],
  missedCalls: {
    data: [],
    pagination: {
      limit: 0,
      offset: 0,
      page: 0,
      totalCount: 0,
      pageSize: 0,
    },
    totalRecords: 0,
  },
  latestSubDispositionId: null,
  callData: null,
  resetFiltersTimestamp: 0,
  // Disposition reminder initial state
  showDispositionReminder: false,
  dispositionReminderData: null,
  // Barge call initial state
  isCallBargingLoading: false,
  callBargingResponse: null,
  // Previous/Next borrower initial state
  isFetchingPreviousOrNextBorrower: false,
  previousOrNextBorrowerResponse: null,
  // Navigation initial state
  navigation: null,
  acwStartTime: null,
  microphoneError: {
    open: false,
    message: '',
  },
  allocatorError: {
    open: false,
    message: '',
  },
  isAgentInManualCall: false,
  isCallBarging: false,
  bargeBorrowerId: '',
  bargeCampaignId: '',
  isUserInitiatedOnlineTransition: false,
  // Priority Tasks initial state
  priorityTasks: {
    data: [],
    pagination: { limit: 10, offset: 0, page: 0 },
    totalRecords: 0,
  },
  priorityTasksSummary: null,
  priorityTasksDistinct: [],
  isPriorityTasksLoading: false,
  isPriorityTasksSummaryLoading: false,
  isPriorityTasksDistinctLoading: false,
  // Missed Calls Distinct
  missedCallsDistinct: [],
  isMissedCallsDistinctLoading: false,
  // WhatsApp Messages
  whatsappMessages: {
    data: [] as IWhatsappMessageRecord[],
    pagination: { page: 0, size: 10 },
    totalElements: 0,
    totalPages: 0,
  },
  whatsappBadgeCount: 0,
  isWhatsappMessagesLoading: false,
  isWhatsappBadgeCountLoading: false,
};

const CRMSlice = createSlice({
  name: 'CRM',
  initialState: crmInitState,
  reducers: {
    setShowIframe(state, action: PayloadAction<boolean>) {
      state.showIframe = action.payload;
    },
    // Fetch campaigns by agent
    fetchCampaignsByAgent: (
      state,
      action: PayloadAction<IFetchCampaignsByAgentRequest>
    ) => {
      state.areCampaignsLoading = true;
      state.error = null;
    },
    fetchCampaignsByAgentSuccess: (
      state,
      action: PayloadAction<IFetchCampaignsByAgentResponse[]>
    ) => {
      state.areCampaignsLoading = false;
      state.campaigns = action.payload;
      state.fetchCampaignsByAgentError = null;
    },
    fetchCampaignsByAgentFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.fetchCampaignsByAgentError = action.payload;
      state.loading = false;
      state.areCampaignsLoading = false;
    },
    // Save campaign
    saveCampaign: (state, action: PayloadAction<ISaveCampaignRequest>) => {
      state.loading = true;
      state.error = null;
      state.saveCampaignCompleted = false;
    },
    saveCampaignSuccess: (
      state,
      action: PayloadAction<ISaveCampaignResponse>
    ) => {
      state.loading = false;
      state.saveCampaignCompleted = true;
    },
    saveCampaignFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
      state.saveCampaignCompleted = true;
    },
    resetSaveCampaignCompleted: (state) => {
      state.saveCampaignCompleted = false;
    },

    // Fetch borrowers in current campaign
    fetchBorrowersInCurrentCampaign: (
      state,
      action: PayloadAction<IFetchBorrowersInCurrentCampaignRequest>
    ) => {
      state.isCRMDataGridLoading = true;
      state.error = null;
    },
    fetchBorrowersInCurrentCampaignSuccess: (
      state,
      action: PayloadAction<IFetchBorrowersInCurrentCampaignResponse>
    ) => {
      state.isCRMDataGridLoading = false;
      state.borrowers = action.payload;
      state.totalRecords = action.payload.pagination.totalCount;
      state.fetchBorrowersByCampaignError = null;
    },
    fetchBorrowersInCurrentCampaignFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.fetchBorrowersByCampaignError = action.payload;
      state.loading = false;
      state.isCRMDataGridLoading = false;
    },

    // Click to call
    clickToCall: (state, action: PayloadAction<IClickToCallRequest>) => {
      state.error = null;
    },
    clickToCallSuccess: (
      state,
      action: PayloadAction<IClickToCallResponse>
    ) => {
      state.clickToCallResponse = action.payload;
    },
    clickToCallFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
    },

    // Fetch borrower info
    fetchBorrowerInfo: (
      state,
      action: PayloadAction<IFetchBorrowerInfoRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchBorrowerInfoSuccess: (
      state,
      action: PayloadAction<IFetchBorrowerInfoResponse>
    ) => {
      state.loading = false;
      state.borrowerInfo = action.payload;
    },
    fetchBorrowerInfoFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Fetch additional info
    fetchAdditionalInfo: (
      state,
      action: PayloadAction<IFetchAdditionalInfoRequest>
    ) => {
      state.areAdditionalInfoLoading = true;
      state.error = null;
    },
    fetchAdditionalInfoSuccess: (
      state,
      action: PayloadAction<IFetchAdditionalInfoResponse>
    ) => {
      state.areAdditionalInfoLoading = false;
      state.additionalInfo = action.payload;
    },
    fetchAdditionalInfoFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.areAdditionalInfoLoading = false;
    },

    // Fetch best and last disposition
    fetchBestAndLastDisposition: (
      state,
      action: PayloadAction<IFetchBestAndLastDispositionRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchBestAndLastDispositionSuccess: (
      state,
      action: PayloadAction<IFetchBestAndLastDispositionResponse>
    ) => {
      state.loading = false;
      state.bestAndLastDisposition = action.payload;
    },
    fetchBestAndLastDispositionFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Search in crm data grid
    searchInCrmDataGrid: (
      state,
      action: PayloadAction<ISearchInCrmDataGridRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    searchInCrmDataGridSuccess: (
      state,
      action: PayloadAction<IFetchBorrowersInCurrentCampaignResponse>
    ) => {
      state.loading = false;
      state.borrowers = action.payload;
    },
    searchInCrmDataGridFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Fetch agent info
    fetchAgentInfo: (state, action: PayloadAction<{ agentId: string }>) => {
      state.loading = true;
      state.error = null;
    },
    fetchAgentInfoSuccess: (state, action: PayloadAction<any>) => {
      state.loading = false;
      state.agentInfo = action.payload;
    },
    fetchAgentInfoFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Add disposition
    addDisposition: (state, action: PayloadAction<IAddDispositionRequest>) => {
      state.isAddDispositionLoading = true;
      state.error = null;
    },
    addDispositionSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.isAddDispositionLoading = false;
      state.message = action.payload.message;
    },
    addDispositionFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.isAddDispositionLoading = false;
    },
    resetAddDispositionMessage: (state) => {
      state.message = '';
    },
    setAcwStartTime: (state, action: PayloadAction<number | null>) => {
      state.acwStartTime = action.payload;
    },
    clearAcwStartTime: (state) => {
      state.acwStartTime = null;
    },
    // Fetch dispositions
    fetchDispositions: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchDispositionsSuccess: (
      state,
      action: PayloadAction<IFetchDispositionsResponse>
    ) => {
      state.loading = false;
      state.dispositions = action.payload;
    },
    fetchDispositionsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Fetch disposition wise summary
    fetchDispositionWiseSummary: (
      state,
      action: PayloadAction<IFetchDispositionWiseSummaryRequest>
    ) => {
      state.isDispositionWiseSummaryLoading = true;
      state.error = null;
    },
    fetchDispositionWiseSummarySuccess: (
      state,
      action: PayloadAction<IFetchDispositionWiseSummaryResponse>
    ) => {
      state.isDispositionWiseSummaryLoading = false;
      state.dispositionWiseSummary = action.payload;
    },
    fetchDispositionWiseSummaryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.isDispositionWiseSummaryLoading = false;
    },

    // Fetch interaction history
    fetchInteractionHistory: (
      state,
      action: PayloadAction<IFetchInteractionHistoryRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchInteractionHistorySuccess: (
      state,
      action: PayloadAction<IFetchInteractionHistoryResponse>
    ) => {
      state.loading = false;
      state.activityHistory = action.payload;
    },
    fetchInteractionHistoryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Fetch follow up time ranges
    fetchFollowUpTimeRanges: (
      state,
      action: PayloadAction<IFetchFollowUpTimeRangesRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchFollowUpTimeRangesSuccess: (
      state,
      action: PayloadAction<IFetchFollowUpTimeRangesResponse[]>
    ) => {
      state.loading = false;
      state.followUpTimeRanges = action.payload;
    },
    fetchFollowUpTimeRangesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Fetch follow up current time
    fetchFollowUpCurrentTime: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchFollowUpCurrentTimeSuccess: (
      state,
      action: PayloadAction<IFetchFollowUpTimeRangesResponse>
    ) => {
      state.loading = false;
      state.followUpCurrentTime = action.payload;
    },
    fetchFollowUpCurrentTimeFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Get unique values by column name
    getUniqueValuesByColumnName: (
      state,
      action: PayloadAction<IGetUniqueValuesByColumnNameRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getUniqueValuesByColumnNameSuccess: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.loading = false;
      state.uniqueColumnValues = action.payload;
    },
    getUniqueValuesByColumnNameFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Search in filter by values
    searchInFilterByValues: (
      state,
      action: PayloadAction<ISearchInFilterByValuesRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    searchInFilterByValuesSuccess: (
      state,
      action: PayloadAction<ISearchInFilterByValuesResponse>
    ) => {
      state.loading = false;
      state.uniqueColumnValues = action.payload.data;
    },
    searchInFilterByValuesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Add alternate number
    addAlternateNumber: (
      state,
      action: PayloadAction<IAddAlternateNumberRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    addAlternateNumberSuccess: (
      state,
      action: PayloadAction<IAddAlternateNumberResponse>
    ) => {
      state.loading = false;
      state.isAddAlternateNumberModalOpen = false;
      state.isSuccessModalOpen = true;
    },
    addAlternateNumberFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Update alternate number
    updateAlternateNumber: (
      state,
      action: PayloadAction<IUpdateAlternateNumberRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateAlternateNumberSuccess: (
      state,
      action: PayloadAction<IUpdateAlternateNumberResponse>
    ) => {
      state.loading = false;
      state.isAddAlternateNumberModalOpen = false;
      state.isSuccessModalOpen = true;
      state.isEditMode = false;
      state.selectedAlternateNumber = '';
    },
    updateAlternateNumberFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // Fetch missed calls
    fetchMissedCalls: (
      state,
      action: PayloadAction<IFetchMissedCallsRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchMissedCallsSuccess: (
      state,
      action: PayloadAction<IFetchMissedCallsResponse>
    ) => {
      state.loading = false;
      state.missedCalls = action.payload;
    },
    fetchMissedCallsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Get remark templates
    getRemarkTemplates: (
      state,
      action: PayloadAction<{ subDispositionId: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    getRemarkTemplatesSuccess: (
      state,
      action: PayloadAction<IGetRemarkTemplatesResponse[]>
    ) => {
      state.loading = false;
      state.remarkTemplates = action.payload;
    },
    getRemarkTemplatesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    resetRemarkTemplates: (state) => {
      state.remarkTemplates = [];
    },

    setCurrentCampaign: (state, action: PayloadAction<Campaign | null>) => {
      state.currentCampaign = action.payload;
    },

    setCurrentDisposition: (
      state,
      action: PayloadAction<DispositionWiseSummary>
    ) => {
      state.currentDisposition = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ limit: number; offset: number }>
    ) => {
      const { limit, offset } = action.payload;
      if (state.borrowers) {
        state.borrowers.pagination = {
          ...state.borrowers.pagination,
          limit,
          offset,
        };
      }
    },
    setCRMActiveTab: (state, action: PayloadAction<CRMTab>) => {
      state.crmActiveTab = action.payload;
    },
    updateAgentState: (state, action: PayloadAction<AgentStateType>) => {
      state.agentState = action.payload;
    },
    // Modal actions
    setContactDetailsModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isContactDetailsModalOpen = action.payload;
    },
    setAddAlternateNumberModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isAddAlternateNumberModalOpen = action.payload;
    },
    setSuccessModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isSuccessModalOpen = action.payload;
    },
    setSelectedAlternateNumber: (state, action: PayloadAction<string>) => {
      state.selectedAlternateNumber = action.payload;
    },
    setIsEditMode: (state, action: PayloadAction<boolean>) => {
      state.isEditMode = action.payload;
    },
    setLatestSubDispositionId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.latestSubDispositionId = action.payload;
    },
    setCallData: (state, action: PayloadAction<CRMCallStatusData | null>) => {
      state.callData = action.payload;
    },
    showDispositionReminder: (
      state,
      action: PayloadAction<DispositionReminderData>
    ) => {
      state.showDispositionReminder = true;
      state.dispositionReminderData = action.payload;
    },
    hideDispositionReminder: (state) => {
      state.showDispositionReminder = false;
      state.dispositionReminderData = null;
    },

    goOnline: (state) => {
      state.isAgentStatusLoading = true;
      state.agentStatusError = null;
    },
    goOnlineSuccess: (state) => {
      state.isAgentStatusLoading = false;
    },
    goOnlineFailure: (state, action: PayloadAction<string>) => {
      state.isAgentStatusLoading = false;
      state.agentStatusError = action.payload;
    },
    goOffline: (state) => {
      state.isAgentStatusLoading = true;
      state.agentStatusError = null;
    },
    goOfflineSuccess: (state) => {
      state.isAgentStatusLoading = false;
    },
    goOfflineFailure: (state, action: PayloadAction<string>) => {
      state.isAgentStatusLoading = false;
      state.agentStatusError = action.payload;
    },
    clearAgentStatusError: (state) => {
      state.agentStatusError = null;
    },
    setAgentLastSavedStatus: (
      state,
      action: PayloadAction<typeof ONLINE | typeof OFFLINE>
    ) => {
      state.agentLastSavedStatus = action.payload;
    },
    setSupervisorLastSavedStatus: (
      state,
      action: PayloadAction<typeof ONLINE | typeof OFFLINE>
    ) => {
      state.supervisorLastSavedStatus = action.payload;
    },
    resetActivityHistoryFilters: (state) => {
      // This action signals components to reset their activity history filters
      // Update timestamp to trigger useEffect in components
      state.resetFiltersTimestamp = Date.now();
    },
    // Navigation actions
    navigateTo: (state, action: PayloadAction<NavigationPayload>) => {
      state.navigation = action.payload;
    },
    clearNavigation: (state) => {
      state.navigation = null;
    },
    // Barge call actions
    callBarging: (state, action: PayloadAction<ICallBargingRequest>) => {
      state.isCallBargingLoading = true;
      state.isCallBarging = true;
      state.bargeBorrowerId = action.payload.borrowerId || '';
      state.bargeCampaignId = action.payload.campaignId || '';
      state.error = null;
    },
    callBargingSuccess: (
      state,
      action: PayloadAction<ICallBargingResponse>
    ) => {
      state.isCallBargingLoading = false;
      state.callBargingResponse = action.payload;
    },
    callBargingFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.isCallBargingLoading = false;
      state.isCallBarging = false;
      state.bargeBorrowerId = '';
      state.bargeCampaignId = '';
      state.error = action.payload;
    },

    // Fetch previous or next borrower actions
    fetchPreviousOrNextBorrower: (
      state,
      action: PayloadAction<IFetchPreviousOrNextBorrowerRequest>
    ) => {
      state.isFetchingPreviousOrNextBorrower = true;
      state.error = null;
    },
    fetchPreviousOrNextBorrowerSuccess: (
      state,
      action: PayloadAction<IFetchPreviousOrNextBorrowerResponse>
    ) => {
      state.isFetchingPreviousOrNextBorrower = false;
      state.previousOrNextBorrowerResponse = action.payload;
    },
    fetchPreviousOrNextBorrowerFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.isFetchingPreviousOrNextBorrower = false;
      state.error = action.payload;
    },
    clearPreviousOrNextBorrowerResponse: (state) => {
      state.previousOrNextBorrowerResponse = null;
    },

    setMicrophoneError: (state, action: PayloadAction<{ message: string }>) => {
      state.microphoneError = {
        open: true,
        message: action.payload.message,
      };
    },

    clearMicrophoneError: (state) => {
      state.microphoneError = {
        open: false,
        message: '',
      };
    },

    setAllocatorError: (state, action: PayloadAction<{ message: string }>) => {
      state.allocatorError = {
        open: true,
        message: action.payload.message,
      };
    },

    clearAllocatorError: (state) => {
      state.allocatorError = {
        open: false,
        message: '',
      };
    },

    setIsAgentInManualCall: (state, action: PayloadAction<boolean>) => {
      state.isAgentInManualCall = action.payload;
    },

    setIsCallBarging: (state, action: PayloadAction<boolean>) => {
      state.isCallBarging = action.payload;
      if (!action.payload) {
        state.bargeBorrowerId = '';
        state.bargeCampaignId = '';
      }
    },

    setUserInitiatedOnlineTransition: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isUserInitiatedOnlineTransition = action.payload;
    },

    // Priority Tasks
    fetchPriorityTasks: (
      state,
      action: PayloadAction<IFetchPriorityTasksRequest>
    ) => {
      state.isPriorityTasksLoading = true;
      state.priorityTasks = {
        data: [],
        pagination: { limit: 20, offset: 0, page: 0 },
        totalRecords: 0,
      };
      state.error = null;
    },
    fetchPriorityTasksSuccess: (
      state,
      action: PayloadAction<IFetchPriorityTasksResponse>
    ) => {
      state.isPriorityTasksLoading = false;
      state.priorityTasks = action.payload;
    },
    fetchPriorityTasksFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.isPriorityTasksLoading = false;
      state.error = action.payload;
    },
    resetPriorityTasks: (state) => {
      state.priorityTasks = {
        data: [],
        pagination: { limit: 20, offset: 0, page: 0 },
        totalRecords: 0,
      };
      state.priorityTasksSummary = null;
    },

    fetchPriorityTasksSummary: (
      state,
      action: PayloadAction<IFetchPriorityTasksSummaryRequest>
    ) => {
      state.isPriorityTasksSummaryLoading = true;
      state.error = null;
    },
    fetchPriorityTasksSummarySuccess: (
      state,
      action: PayloadAction<PriorityTasksSummary>
    ) => {
      state.isPriorityTasksSummaryLoading = false;
      state.priorityTasksSummary = action.payload;
    },
    fetchPriorityTasksSummaryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.isPriorityTasksSummaryLoading = false;
      state.error = action.payload;
    },

    fetchPriorityTasksDistinct: (
      state,
      action: PayloadAction<IFetchPriorityTasksDistinctRequest>
    ) => {
      state.isPriorityTasksDistinctLoading = true;
      state.priorityTasksDistinct = [];
      state.error = null;
    },
    fetchPriorityTasksDistinctSuccess: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.isPriorityTasksDistinctLoading = false;
      state.priorityTasksDistinct = action.payload;
    },
    fetchPriorityTasksDistinctFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.isPriorityTasksDistinctLoading = false;
      state.error = action.payload;
    },

    // Missed Calls Distinct
    fetchMissedCallsDistinct: (
      state,
      _action: PayloadAction<IFetchMissedCallsDistinctRequest>
    ) => {
      state.isMissedCallsDistinctLoading = true;
      state.missedCallsDistinct = [];
      state.error = null;
    },
    fetchMissedCallsDistinctSuccess: (
      state,
      action: PayloadAction<(string | MissedCallDistinctItemWithId)[]>
    ) => {
      state.isMissedCallsDistinctLoading = false;
      state.missedCallsDistinct = action.payload;
    },
    fetchMissedCallsDistinctFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.isMissedCallsDistinctLoading = false;
      state.error = action.payload;
    },

    // WhatsApp Messages
    fetchWhatsappMessages: (
      state,
      _action: PayloadAction<IFetchWhatsappMessagesRequest>
    ) => {
      state.isWhatsappMessagesLoading = true;
      state.error = null;
    },
    fetchWhatsappMessagesSuccess: (
      state,
      action: PayloadAction<IWhatsappMessagesResponse>
    ) => {
      state.isWhatsappMessagesLoading = false;
      state.whatsappMessages.data = action.payload.messages;
      state.whatsappMessages.totalElements =
        action.payload.pagination.totalCount;
      state.whatsappMessages.totalPages = action.payload.pagination.totalPages;
      state.whatsappMessages.pagination.page = action.payload.pagination.page;
    },
    fetchWhatsappMessagesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.isWhatsappMessagesLoading = false;
      state.error = action.payload;
    },
    resetWhatsappMessages: (state) => {
      state.whatsappMessages = {
        data: [],
        pagination: { page: 0, size: 10 },
        totalElements: 0,
        totalPages: 0,
      };
    },
    fetchWhatsappBadgeCount: (
      state,
      _action: PayloadAction<{ date: string }>
    ) => {
      state.isWhatsappBadgeCountLoading = true;
    },
    fetchWhatsappBadgeCountSuccess: (
      state,
      action: PayloadAction<IFetchWhatsappBadgeCountResponse>
    ) => {
      state.isWhatsappBadgeCountLoading = false;
      state.whatsappBadgeCount = action.payload.count;
    },
    fetchWhatsappBadgeCountFailure: (
      state,
      _action: PayloadAction<IFailedPayload>
    ) => {
      state.isWhatsappBadgeCountLoading = false;
    },
  },
});

export const {
  setShowIframe,
  fetchCampaignsByAgent,
  fetchCampaignsByAgentSuccess,
  fetchCampaignsByAgentFailure,
  saveCampaign,
  saveCampaignSuccess,
  saveCampaignFailure,
  resetSaveCampaignCompleted,
  fetchBorrowersInCurrentCampaign,
  fetchBorrowersInCurrentCampaignSuccess,
  fetchBorrowersInCurrentCampaignFailure,
  clickToCall,
  clickToCallSuccess,
  clickToCallFailure,
  fetchBorrowerInfo,
  fetchBorrowerInfoSuccess,
  fetchBorrowerInfoFailure,
  fetchAdditionalInfo,
  fetchAdditionalInfoSuccess,
  fetchAdditionalInfoFailure,
  searchInCrmDataGrid,
  searchInCrmDataGridSuccess,
  searchInCrmDataGridFailure,
  fetchAgentInfo,
  fetchAgentInfoSuccess,
  fetchAgentInfoFailure,
  addDisposition,
  addDispositionSuccess,
  addDispositionFailure,
  resetAddDispositionMessage,
  setAcwStartTime,
  clearAcwStartTime,
  fetchDispositions,
  fetchDispositionsSuccess,
  fetchDispositionsFailure,
  fetchDispositionWiseSummary,
  fetchDispositionWiseSummarySuccess,
  fetchDispositionWiseSummaryFailure,
  fetchBestAndLastDisposition,
  fetchBestAndLastDispositionSuccess,
  fetchBestAndLastDispositionFailure,
  fetchInteractionHistory,
  fetchInteractionHistorySuccess,
  fetchInteractionHistoryFailure,
  fetchFollowUpTimeRanges,
  fetchFollowUpTimeRangesSuccess,
  fetchFollowUpTimeRangesFailure,
  fetchFollowUpCurrentTime,
  fetchFollowUpCurrentTimeSuccess,
  fetchFollowUpCurrentTimeFailure,
  getUniqueValuesByColumnName,
  getUniqueValuesByColumnNameSuccess,
  getUniqueValuesByColumnNameFailure,
  searchInFilterByValues,
  searchInFilterByValuesSuccess,
  searchInFilterByValuesFailure,
  addAlternateNumber,
  addAlternateNumberSuccess,
  addAlternateNumberFailure,
  updateAlternateNumber,
  updateAlternateNumberSuccess,
  updateAlternateNumberFailure,
  getRemarkTemplates,
  getRemarkTemplatesSuccess,
  getRemarkTemplatesFailure,
  resetRemarkTemplates,
  fetchMissedCalls,
  fetchMissedCallsSuccess,
  fetchMissedCallsFailure,
  fetchMissedCallsDistinct,
  fetchMissedCallsDistinctSuccess,
  fetchMissedCallsDistinctFailure,
  setCurrentCampaign,
  setCurrentDisposition,
  setPagination,
  setCRMActiveTab,
  updateAgentState,
  setContactDetailsModalOpen,
  setAddAlternateNumberModalOpen,
  setSuccessModalOpen,
  setSelectedAlternateNumber,
  setIsEditMode,
  setLatestSubDispositionId,
  setCallData,
  showDispositionReminder,
  hideDispositionReminder,
  goOnline,
  goOnlineSuccess,
  goOnlineFailure,
  goOffline,
  goOfflineSuccess,
  goOfflineFailure,
  clearAgentStatusError,
  setAgentLastSavedStatus,
  setSupervisorLastSavedStatus,
  resetActivityHistoryFilters,
  callBarging,
  callBargingSuccess,
  callBargingFailure,
  fetchPreviousOrNextBorrower,
  fetchPreviousOrNextBorrowerSuccess,
  fetchPreviousOrNextBorrowerFailure,
  clearPreviousOrNextBorrowerResponse,
  navigateTo,
  clearNavigation,
  setMicrophoneError,
  clearMicrophoneError,
  setAllocatorError,
  clearAllocatorError,
  setIsAgentInManualCall,
  setIsCallBarging,
  setUserInitiatedOnlineTransition,
  fetchPriorityTasks,
  fetchPriorityTasksSuccess,
  fetchPriorityTasksFailure,
  resetPriorityTasks,
  fetchPriorityTasksSummary,
  fetchPriorityTasksSummarySuccess,
  fetchPriorityTasksSummaryFailure,
  fetchPriorityTasksDistinct,
  fetchPriorityTasksDistinctSuccess,
  fetchPriorityTasksDistinctFailure,
  fetchWhatsappMessages,
  fetchWhatsappMessagesSuccess,
  fetchWhatsappMessagesFailure,
  resetWhatsappMessages,
  fetchWhatsappBadgeCount,
  fetchWhatsappBadgeCountSuccess,
  fetchWhatsappBadgeCountFailure,
} = CRMSlice.actions;

export default CRMSlice.reducer;
